/**
 * ServiceTemplates — Settings editor for the email + invoice verbiage
 * templates used by Service Tickets. Placeholders merge at use time.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Mail, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const PLACEHOLDERS = ["{ticket_number}", "{property_name}", "{description}", "{resolution_notes}", "{reported_by}", "{issue_type}"];
const inputCls = "w-full h-10 px-3 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1";
const empty = { kind: "email", name: "", subject: "", body: "", sort_order: 0 };

export default function ServiceTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = () => api.get("/service-templates").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = (kind) => { setEditing(null); setForm({ ...empty, kind }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ kind: t.kind, name: t.name, subject: t.subject || "", body: t.body || "", sort_order: t.sort_order || 0 }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.patch(`/service-templates/${editing.id}`, form);
      else await api.post("/service-templates", form);
      toast.success("Template saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.delete(`/service-templates/${confirmTarget.id}`);
    setConfirmTarget(null);
    toast.success("Template deleted");
    load();
  };

  const insertPlaceholder = (p) => setForm({ ...form, body: (form.body || "") + p });

  const Section = ({ kind, title, icon: Icon }) => {
    const rows = items.filter((t) => t.kind === kind);
    return (
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid={`templates-section-${kind}`}>
        <div className="px-5 py-3 border-b-2 border-zinc-950 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-black tracking-tight"><Icon className="w-4 h-4 text-blue-700" /> {title}</div>
          <button data-testid={`new-template-${kind}`} onClick={() => openNew(kind)} className="inline-flex items-center gap-1.5 h-8 px-3 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No templates yet.</div>
        ) : rows.map((t) => (
          <div key={t.id} className="px-5 py-3 border-b border-zinc-100 last:border-b-0 flex items-start gap-3 hover:bg-zinc-50" data-testid={`template-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">{t.name}</div>
              {t.kind === "email" && t.subject && <div className="text-xs text-zinc-500 truncate mt-0.5">Subject: {t.subject}</div>}
              <div className="text-xs text-zinc-500 truncate mt-0.5">{(t.body || "").slice(0, 120)}…</div>
            </div>
            <button data-testid={`edit-template-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 hover:bg-zinc-200 rounded-sm"><Pencil className="w-3.5 h-3.5" /></button>
            <button data-testid={`delete-template-${t.id}`} onClick={() => setConfirmTarget(t)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="service-templates-page">
      <h1 className="font-heading text-3xl font-black tracking-tight">Service Templates</h1>
      <p className="text-sm text-zinc-500 mt-1 mb-2">Email and invoice wording used by Service Tickets — write your own, in your voice.</p>
      <div className="text-xs text-zinc-500 mb-6">
        Available placeholders (filled from the ticket automatically):{" "}
        {PLACEHOLDERS.map((p) => <code key={p} className="mx-1 px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm font-mono text-[11px]">{p}</code>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section kind="email" title="Email Templates" icon={Mail} />
        <Section kind="invoice" title="Invoice Verbiage" icon={Receipt} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing ? "Edit Template" : `New ${form.kind === "email" ? "Email" : "Invoice"} Template`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="template-form">
            <div>
              <label className={labelCls}>Template Name *</label>
              <input data-testid="template-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='e.g. "Storm damage — insurance referral"' />
            </div>
            {form.kind === "email" && (
              <div>
                <label className={labelCls}>Subject</label>
                <input data-testid="template-subject" className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
            )}
            <div>
              <label className={labelCls}>{form.kind === "email" ? "Email Body" : "Invoice Line Description"}</label>
              <textarea data-testid="template-body" required rows={form.kind === "email" ? 12 : 6} className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 font-mono" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {PLACEHOLDERS.map((p) => (
                  <button key={p} type="button" onClick={() => insertPlaceholder(p)} className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm font-mono text-[10px] hover:border-blue-700 hover:text-blue-700">{p}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
              <button type="submit" disabled={saving} data-testid="save-template-button" className="inline-flex items-center gap-2 h-10 px-5 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Template
              </button>
              <button type="button" onClick={() => setOpen(false)} className="h-10 px-3 text-xs text-zinc-500 hover:text-zinc-950">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        title="Delete this template?"
        message={`"${confirmTarget?.name || ""}" will be removed.`}
        onConfirm={remove}
      />
    </div>
  );
}
